/**
 * WhatsAppInboxPage — staff-facing two-way WhatsApp inbox (Meta Cloud API).
 * Reuses the same `wa-*` chat CSS as the patient-portal CareChatView for a
 * native WhatsApp look, but talks to its own backend routes (routes/whatsapp.py)
 * since this is a different conversation source (real Meta messages, not the
 * in-app patient<->staff chat).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Loader2, Search, ArrowLeft, MessagesSquare, Clock, FileStack } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import '../care/care.css';

const POLL_MS = 6000;

const initials = (label = '?') => (label || '?').trim().charAt(0).toUpperCase() || '?';

const stampLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const dayLabel = (iso) => {
  const d = new Date(iso.slice(0, 10) + 'T00:00:00');
  const today = new Date();
  const diff = Math.round((new Date(today.toDateString()) - new Date(d.toDateString())) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
};

const ThreadRow = ({ thread, active, onOpen }) => (
  <button
    type="button" onClick={() => onOpen(thread.wa_id)}
    className={`wa-row ${active ? 'wa-row-active' : ''}`} data-testid={`whatsapp-thread-${thread.wa_id}`}
  >
    <span className="wa-avatar" aria-hidden="true">{initials(thread.wa_id)}</span>
    <span className="wa-row-main">
      <span className="wa-row-top">
        <span className="wa-row-name" data-testid={`whatsapp-thread-name-${thread.wa_id}`}>+{thread.wa_id}</span>
        <span className="wa-row-time">{stampLabel(thread.last_message_at)}</span>
      </span>
      <span className="wa-row-bottom">
        <span className="wa-row-preview">{thread.last_message_preview || 'No messages yet'}</span>
        {thread.unread_by_staff > 0 && (
          <span className="wa-row-badge" data-testid={`whatsapp-thread-unread-${thread.wa_id}`}>
            {thread.unread_by_staff > 99 ? '99+' : thread.unread_by_staff}
          </span>
        )}
      </span>
    </span>
  </button>
);

const MessageBubble = ({ message, showDaySeparator }) => (
  <>
    {showDaySeparator && <p className="wa-day">{dayLabel(message.created_at)}</p>}
    <article
      className={`wa-bubble ${message.direction === 'outbound' ? 'wa-bubble-mine' : 'wa-bubble-theirs'}`}
      data-testid={`whatsapp-message-${message.message_id}`}
    >
      <p className="wa-bubble-text">{message.body}</p>
      <span className="wa-meta">
        {new Date(message.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        {message.direction === 'outbound' && message.status === 'failed' && (
          <span className="text-red-500 ml-1" title={message.error}>· failed</span>
        )}
      </span>
    </article>
  </>
);

export default function WhatsAppInboxPage() {
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [activeWaId, setActiveWaId] = useState('');
  const [messages, setMessages] = useState([]);
  const [windowOpen, setWindowOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [query, setQuery] = useState('');
  const [paneOpen, setPaneOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesError, setTemplatesError] = useState('');
  const [templateName, setTemplateName] = useState('');
  const scrollRef = useRef(null);
  const hadError = useRef(false);

  const loadThreads = () => api.get('/whatsapp/threads').then((r) => {
    setThreads(r.data.threads || []);
    hadError.current = false;
  }).catch(() => {
    if (!hadError.current) { toast.error("Couldn't load WhatsApp conversations"); hadError.current = true; }
  }).finally(() => setThreadsLoading(false));
  const loadMessages = (waId) => api.get(`/whatsapp/threads/${waId}/messages`).then((r) => {
    setMessages(r.data.messages || []);
    setWindowOpen(!!r.data.service_window_open);
    setThreads((prev) => prev.map((t) => (t.wa_id === waId ? { ...t, unread_by_staff: 0 } : t)));
    window.dispatchEvent(new Event('whatsapp:unread-changed'));
  }).catch(() => toast.error("Couldn't load this conversation"));

  useEffect(() => {
    loadThreads();
    api.get('/whatsapp/templates').then((r) => {
      setTemplates(r.data.templates || []);
      setTemplatesError(r.data.ok === false ? (r.data.error || 'Could not load templates') : '');
    }).catch(() => setTemplatesError('Could not load templates'));
    const timer = setInterval(loadThreads, POLL_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!activeWaId) return;
    loadMessages(activeWaId);
    const timer = setInterval(() => loadMessages(activeWaId), POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWaId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => `${t.wa_id} ${t.last_message_preview || ''}`.toLowerCase().includes(q));
  }, [threads, query]);

  const openThread = (waId) => { setActiveWaId(waId); setPaneOpen(true); };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/whatsapp/threads/${activeWaId}/reply`, { body: text.trim() });
      setText('');
      await loadMessages(activeWaId);
      await loadThreads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const sendTemplate = async () => {
    if (!templateName) { toast.error('Pick a template first'); return; }
    setSending(true);
    try {
      await api.post('/whatsapp/send-template', { to: activeWaId, template_name: templateName });
      toast.success('Template sent');
      await loadMessages(activeWaId);
      await loadThreads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`wa-shell ${paneOpen ? 'wa-pane-open' : ''}`} data-testid="whatsapp-inbox-shell">
      <aside className="wa-list" data-testid="whatsapp-inbox-list">
        <header className="wa-list-head">
          <h2 className="flex items-center gap-2"><MessagesSquare size={17} /> WhatsApp</h2>
          <div className="wa-search">
            <Search size={15} />
            <input
              value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search conversations"
              aria-label="Search conversations" data-testid="whatsapp-inbox-search"
            />
          </div>
        </header>
        <div className="wa-list-scroll">
          {rows.map((t) => (
            <ThreadRow key={t.wa_id} thread={t} active={t.wa_id === activeWaId} onOpen={openThread} />
          ))}
          {!rows.length && threadsLoading && (
            <p className="wa-list-empty" data-testid="whatsapp-inbox-loading">Loading conversations…</p>
          )}
          {!rows.length && !threadsLoading && (
            <p className="wa-list-empty" data-testid="whatsapp-inbox-empty">
              {query.trim()
                ? 'No matching conversations'
                : 'No conversations yet — messages your customers send will show up here.'}
            </p>
          )}
        </div>
      </aside>

      <section className="wa-pane" data-testid="whatsapp-inbox-pane">
        <header className="wa-pane-head">
          <button type="button" className="wa-back" onClick={() => setPaneOpen(false)} aria-label="Back to conversations" data-testid="whatsapp-inbox-back">
            <ArrowLeft size={18} />
          </button>
          <span className="wa-avatar" aria-hidden="true">{initials(activeWaId)}</span>
          <div className="wa-pane-id">
            <strong data-testid="whatsapp-inbox-pane-title">{activeWaId ? `+${activeWaId}` : 'Select a conversation'}</strong>
            {activeWaId && (
              <span className="flex items-center gap-1">
                {windowOpen ? 'Can reply freely (within 24h window)' : <><Clock size={12} /> 24h window closed — send a template</>}
              </span>
            )}
          </div>
        </header>

        <div ref={scrollRef} className="wa-canvas" data-testid="whatsapp-inbox-messages">
          {messages.map((m, i) => (
            <MessageBubble key={m.message_id} message={m} showDaySeparator={i === 0 || messages[i - 1].created_at.slice(0, 10) !== m.created_at.slice(0, 10)} />
          ))}
        </div>

        {!activeWaId ? (
          <div className="wa-composer wa-composer-locked" data-testid="whatsapp-inbox-locked">Select a conversation to reply</div>
        ) : windowOpen ? (
          <form className="wa-composer" onSubmit={sendReply}>
            <div className="wa-composer-row">
              <Textarea
                rows={1} value={text} onChange={(e) => setText(e.target.value)} maxLength={4000}
                placeholder="Type a message" className="wa-input" data-testid="whatsapp-inbox-input"
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) sendReply(e); }}
              />
              <button type="submit" className="wa-send" disabled={sending || !text.trim()} aria-label="Send message" data-testid="whatsapp-inbox-send">
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </div>
          </form>
        ) : (
          <div className="wa-composer flex items-center gap-2 flex-wrap" data-testid="whatsapp-inbox-template-composer">
            <FileStack size={16} className="text-muted-foreground shrink-0" />
            <Select value={templateName} onValueChange={setTemplateName}>
              <SelectTrigger className="flex-1 min-w-[160px] h-9" data-testid="whatsapp-inbox-template-select">
                <SelectValue placeholder={
                  templates.length ? 'Pick an approved template' : (templatesError || 'No approved templates found')
                } />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.name} value={t.name}>{t.name} ({t.status})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={sendTemplate} disabled={sending || !templateName} data-testid="whatsapp-inbox-template-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send template'}
            </Button>
            {templatesError && (
              <p className="w-full text-[11px] text-red-500" data-testid="whatsapp-inbox-templates-error">{templatesError}</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
